import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_equal, assert_instanceof } from '@shared/asserts/utils_asserts';
import { DTO__image_crop_box } from '@shared/dto/+image/image_asset_base/DTO__image_crop_box';
import { ENUM__invocation_type } from '@shared/dto/generation/graph_invoke/_invocations/_base/ENUM__invocation_type';
import { INVOCATION_IMAGE__viewport } from '@shared/dto/generation/graph_invoke/_invocations/image/INVOCATION_IMAGE__viewport';
import { useEffect, useState } from 'react';
import ReactCrop, { PixelCrop } from 'react-image-crop';
import { HOOK__get_div_size } from '../../../_hooks/HOOK__get_div_size';
import { G_IMAGE__master_component } from '../../nodes/node/fields/image/_views/G_IMAGE__master_component';
import { G_LAYOUT__image_edit_base } from '../_base/G_LAYOUT__image_edit_base';
import { HOOK_REACT_CROP__cursors } from './HOOK_REACT_CROP__cursors';

export function G_LAYOUT__image_edit_crop_box() {
  /*
   * STATE
   */

  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__ui_disabled, g_image_edit__field: field } = use_slice_ui(['g_general__ui_disabled', 'g_image_edit__field']);

  let { graph } = generation_current.tool;

  /*
   * STORE
   */

  const { ref_element__set, size: image_display_size } = HOOK__get_div_size();

  /*
   * STATE
   */
  const [crop_box_scaled, set_crop_box_scaled] = useState(DTO__image_crop_box.ZERO.clone());

  HOOK_REACT_CROP__cursors();

  /*
   * EFFECTS
   */

  useEffect(() => {
    if (!field.image__is_initialized) return try_set_crop_box_scaled(DTO__image_crop_box.ZERO.clone());
    if (field.meta.node_type != ENUM__invocation_type.IMAGE__VIEWPORT) return try_set_crop_box_scaled(DTO__image_crop_box.ZERO.clone());

    let node = graph.node__get(field.meta.node_id);
    assert_instanceof(node, INVOCATION_IMAGE__viewport);
    let crop_box = node.crop_box.crop_box;

    if (crop_box.is_stub()) return try_set_crop_box_scaled(DTO__image_crop_box.ZERO.clone());
    if (image_display_size.is_zero()) return try_set_crop_box_scaled(DTO__image_crop_box.ZERO.clone());

    const scaled = DTO__image_crop_box.original_to_scaled(crop_box, image_display_size, field.asset.image_size);

    try_set_crop_box_scaled(scaled);
  }, [field.image__is_initialized, field, image_display_size, graph]);

  /*
   * EVENTS
   */

  async function on_crop_selection_double_click(e: React.MouseEvent<HTMLDivElement>) {
    if (g_general__ui_disabled) return;

    e.preventDefault();
    e.stopPropagation();

    let node = graph.node__get(field.meta.node_id);
    assert_instanceof(node, INVOCATION_IMAGE__viewport);

    let graph_new = graph.clone();

    node.cropbox_maximize(graph_new);

    await UTILS__graph_data.update(graph_new);
  }

  function on_crop_change(crop_data: PixelCrop) {
    if (g_general__ui_disabled) return;

    // todo:review, need get original size of cropbox, not scaled
    if (crop_data.width < CONSTANTS__client.CROP_BOX_SIDE_MIN) return;
    if (crop_data.height < CONSTANTS__client.CROP_BOX_SIDE_MIN) return;

    let crop_box_new = DTO__image_crop_box.from_react_crop_object(crop_data);

    set_crop_box_scaled(crop_box_new);
  }

  async function on_crop_end(crop_data: PixelCrop) {
    if (g_general__ui_disabled) return;

    assert_equal(field.meta.node_type, ENUM__invocation_type.IMAGE__VIEWPORT);
    let node = graph.node__get(field.meta.node_id);
    assert_instanceof(node, INVOCATION_IMAGE__viewport);

    let crop_box = node.crop_box.crop_box;

    // todo:review, need get original size of cropbox, not scaled
    if (crop_data.width < CONSTANTS__client.CROP_BOX_SIDE_MIN) return;
    if (crop_data.height < CONSTANTS__client.CROP_BOX_SIDE_MIN) return;

    let crop_box_new = DTO__image_crop_box.from_react_crop_object(crop_data);

    set_crop_box_scaled(crop_box_new);

    let original_image_size = field.get_image_size();

    let crop_box_original_raw = DTO__image_crop_box.scaled_to_original(crop_box_new, image_display_size.clone(), original_image_size);

    /*
     * 2. ALIGN TO MULTIPLE OF 8 & CLAMP BY IMAGE SIZE
     */

    // Snap and clamp coordinates (X, Y)
    const snapped_x = crop_box_original_raw.x.snap_to_8_and_clamp(0, original_image_size.width);
    const snapped_y = crop_box_original_raw.y.snap_to_8_and_clamp(0, original_image_size.height);

    // Snap and clamp dimensions (Width, Height) ensuring they don't overflow the image canvas
    const snapped_width = crop_box_original_raw.width.snap_to_8_and_clamp(CONSTANTS__client.CROP_BOX_SIDE_MIN, original_image_size.width - snapped_x);
    const snapped_height = crop_box_original_raw.height.snap_to_8_and_clamp(CONSTANTS__client.CROP_BOX_SIDE_MIN, original_image_size.height - snapped_y);

    // Instantiate your final cleaned-up DTO
    // Note: Adjust instantiation syntax depending on how DTO__image_crop_box constructs objects (e.g., new DTO__image_crop_box(...) or a factory method)
    let crop_box_original = new DTO__image_crop_box().clone({
      x: snapped_x,
      y: snapped_y,
      width: snapped_width,
      height: snapped_height,
    });

    if (crop_box.is_equals(crop_box_original)) return;

    /*
     * VARIANCE THRESHOLD CHECK
     */

    // Calculate the absolute delta sum of X, Y, Width, and Height changes
    const total_pixel_difference = crop_box.difference(crop_box_original);

    // Establish a benchmark based on the original image's total dimensions
    const original_total_dimension = original_image_size.width + original_image_size.height;

    // Determine the threshold value
    const threshold = original_total_dimension * 0.02;

    // Abort backend synchronization if the change does not exceed treshhold
    if (total_pixel_difference < threshold) {
      set_crop_box_scaled(DTO__image_crop_box.original_to_scaled(crop_box, image_display_size, original_image_size));
      return;
    }

    /*
     * BACKEND
     */
    let field_new = node.crop_box.clone();
    field_new.crop_box = crop_box_original.clone();

    await UTILS__graph_data.update_field(graph, field_new);
  }

  function try_set_crop_box_scaled(value: DTO__image_crop_box) {
    if (value.is_equals(crop_box_scaled)) return;

    set_crop_box_scaled(value);
  }

  /*
   * HTML
   */

  if (field.meta.node_type != ENUM__invocation_type.IMAGE__VIEWPORT) return null;

  // do not return null if crop_box_scaled is zero or stub - in that case we cant init crop_box because it relies on div

  let node = graph.node__get(field.meta.node_id);
  assert_instanceof(node, INVOCATION_IMAGE__viewport);

  return (
    <G_LAYOUT__image_edit_base>
      <G_IMAGE__master_component {...{ field }}>
        <div onDoubleClick={e => void on_crop_selection_double_click(e)}>
          <ReactCrop
            aspect={graph.invocation_grid2d_view.grid2d_cell__aspect_ratio}
            keepSelection
            disabled={g_general__ui_disabled}
            crop={crop_box_scaled.to_react_crop_object()}
            onChange={on_crop_change}
            onComplete={on_crop_end}
            className={`!absolute top-0 size-full ${g_general__ui_disabled ? '' : 'disable-crop-transitions'}`}
          >
            <div ref={ref_element__set} className="info__size_detector size-full pointer-events-none opacity-0" />
          </ReactCrop>
        </div>
      </G_IMAGE__master_component>
    </G_LAYOUT__image_edit_base>
  );
}
